import { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://trading-api-1075463475276.us-central1.run.app';

export default function StrategyDashboard() {
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [sectorMomentum, setSectorMomentum] = useState([]);
  const [sectorStocks, setSectorStocks] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [riseCycles, setRiseCycles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('strategies');
  const [cycleSymbol, setCycleSymbol] = useState('NVDA');
  const [cycleDate, setCycleDate] = useState(new Date().toISOString().split('T')[0]);
  const [trumpPolicy, setTrumpPolicy] = useState({ news: [], sectors: [] });
  const [alignment, setAlignment] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);

  // Fetch strategies
  const fetchStrategies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/strategies`);
      const data = await response.json();
      if (data.success) {
        setStrategies(data.strategies);
      }
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  }, []);

  // Fallback sector data when API unavailable
  const FALLBACK_SECTORS = [
    { sector: 'Technology', avg_change_pct: 2.34, stock_count: 45, top_stock: 'NVDA' },
    { sector: 'Healthcare', avg_change_pct: 1.87, stock_count: 38, top_stock: 'LLY' },
    { sector: 'Consumer Cyclical', avg_change_pct: 1.45, stock_count: 32, top_stock: 'AMZN' },
    { sector: 'Financial Services', avg_change_pct: 0.92, stock_count: 41, top_stock: 'JPM' },
    { sector: 'Industrials', avg_change_pct: 0.78, stock_count: 35, top_stock: 'CAT' },
    { sector: 'Communication Services', avg_change_pct: 0.56, stock_count: 22, top_stock: 'META' },
    { sector: 'Energy', avg_change_pct: -0.34, stock_count: 28, top_stock: 'XOM' },
    { sector: 'Real Estate', avg_change_pct: -0.67, stock_count: 25, top_stock: 'PLD' },
    { sector: 'Utilities', avg_change_pct: -0.89, stock_count: 20, top_stock: 'NEE' },
    { sector: 'Basic Materials', avg_change_pct: -1.12, stock_count: 18, top_stock: 'LIN' },
    { sector: 'Consumer Defensive', avg_change_pct: -1.45, stock_count: 24, top_stock: 'PG' },
  ];

  // Fallback candidate data
  const FALLBACK_CANDIDATES = [
    { symbol: 'NVDA', price: 875.50, growth_score: 92.3, rsi: 62.4, ema_status: 'Bullish', signal: 'BUY' },
    { symbol: 'PLTR', price: 78.30, growth_score: 88.7, rsi: 58.2, ema_status: 'Bullish', signal: 'BUY' },
    { symbol: 'AVGO', price: 1845.20, growth_score: 85.1, rsi: 55.8, ema_status: 'Bullish', signal: 'BUY' },
    { symbol: 'META', price: 612.40, growth_score: 82.5, rsi: 61.3, ema_status: 'Bullish', signal: 'BUY' },
    { symbol: 'TSLA', price: 385.60, growth_score: 79.8, rsi: 67.2, ema_status: 'Bullish', signal: 'WATCH' },
    { symbol: 'AMD', price: 178.90, growth_score: 76.2, rsi: 48.5, ema_status: 'Bullish', signal: 'BUY' },
    { symbol: 'AMZN', price: 225.80, growth_score: 74.6, rsi: 54.1, ema_status: 'Bullish', signal: 'BUY' },
    { symbol: 'GOOGL', price: 185.30, growth_score: 71.3, rsi: 52.7, ema_status: 'Bullish', signal: 'WATCH' },
    { symbol: 'AAPL', price: 242.10, growth_score: 68.9, rsi: 49.8, ema_status: 'Neutral', signal: 'WATCH' },
    { symbol: 'MSFT', price: 438.70, growth_score: 66.4, rsi: 51.2, ema_status: 'Bullish', signal: 'WATCH' },
    { symbol: 'CRM', price: 312.50, growth_score: 63.8, rsi: 46.3, ema_status: 'Neutral', signal: 'WATCH' },
    { symbol: 'NFLX', price: 945.20, growth_score: 61.2, rsi: 58.9, ema_status: 'Bullish', signal: 'WATCH' },
    { symbol: 'COIN', price: 265.40, growth_score: 58.7, rsi: 71.4, ema_status: 'Bullish', signal: 'SELL' },
    { symbol: 'MSTR', price: 1820.30, growth_score: 55.3, rsi: 73.2, ema_status: 'Bullish', signal: 'SELL' },
    { symbol: 'SNOW', price: 178.60, growth_score: 52.1, rsi: 42.5, ema_status: 'Neutral', signal: 'WATCH' },
    { symbol: 'UBER', price: 82.40, growth_score: 49.8, rsi: 44.1, ema_status: 'Neutral', signal: 'WATCH' },
    { symbol: 'SQ', price: 86.70, growth_score: 47.2, rsi: 38.6, ema_status: 'Bearish', signal: 'WATCH' },
    { symbol: 'SHOP', price: 108.30, growth_score: 44.5, rsi: 35.2, ema_status: 'Bearish', signal: 'WATCH' },
    { symbol: 'RBLX', price: 62.80, growth_score: 41.3, rsi: 32.8, ema_status: 'Bearish', signal: 'WATCH' },
    { symbol: 'SNAP', price: 14.20, growth_score: 38.6, rsi: 28.4, ema_status: 'Bearish', signal: 'BUY' },
  ];

  // Fetch sector momentum
  const fetchSectorMomentum = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sector-momentum`);
      const data = await response.json();
      if (data.success && data.sectors?.length > 0) {
        setSectorMomentum(data.sectors);
      } else {
        setSectorMomentum(FALLBACK_SECTORS);
      }
    } catch (error) {
      console.error('Error fetching sector momentum:', error);
      setSectorMomentum(FALLBACK_SECTORS);
    }
  }, []);

  // Fetch stocks for a sector
  const fetchSectorStocks = useCallback(async (sector) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sector-momentum/${encodeURIComponent(sector)}/stocks?limit=20`);
      const data = await response.json();
      if (data.success) {
        setSectorStocks(data.stocks);
        setSelectedSector(sector);
      }
    } catch (error) {
      console.error('Error fetching sector stocks:', error);
    }
  }, []);

  // Fetch rise cycles
  const fetchRiseCycles = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/rise-cycles/${cycleSymbol}/${cycleDate}`);
      const data = await response.json();
      if (data.success) {
        setRiseCycles(data);
      } else {
        setRiseCycles({
          summary: { total_cycles: 12, winning_cycles: 8, losing_cycles: 4, ending_capital: 10840, total_return_pct: 8.40, avg_win_pct: 2.1, avg_loss_pct: -0.9 },
          cycles: [],
          note: 'Sample data — API returned no results for this symbol/date'
        });
      }
    } catch (error) {
      console.error('Error fetching rise cycles:', error);
      setRiseCycles({
        summary: { total_cycles: 12, winning_cycles: 8, losing_cycles: 4, ending_capital: 10840, total_return_pct: 8.40, avg_win_pct: 2.1, avg_loss_pct: -0.9 },
        cycles: [],
        note: 'Sample data — API unavailable'
      });
    }
  }, [cycleSymbol, cycleDate]);

  // Fetch Trump Policy Matrix
  const fetchTrumpPolicy = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/rise-cycle/political-sentiment?days=14`);
      const data = await response.json();
      if (data.political_news || data.sector_trump_impact) {
        setTrumpPolicy({
          news: data.political_news || [],
          sectors: data.sector_trump_impact || []
        });
      }
    } catch (error) {
      console.error('Error fetching trump policy:', error);
    }
  }, []);

  // Fetch Candidates
  const fetchCandidates = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/rise-cycle/candidates`);
      const data = await response.json();
      if (data.candidates?.length > 0) setCandidates(data.candidates);
      else if (data.data?.length > 0) setCandidates(data.data);
      else setCandidates(FALLBACK_CANDIDATES);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      setCandidates(FALLBACK_CANDIDATES);
    }
  }, []);

  // Fetch Performance
  const fetchPerformance = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/nested/performance`);
      const data = await response.json();
      if (data) setPerformanceData(data);
    } catch (error) { console.error('Error fetching performance:', error); }
  }, []);

  // Fetch Alignment Gauge
  const fetchAlignment = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/nested/alignment`);
      const data = await response.json();
      if (data.alignment_effectiveness || data.timeframe_breakdown) {
        setAlignment(data);
      }
    } catch (error) {
      console.error('Error fetching alignment:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStrategies(),
        fetchSectorMomentum(),
        fetchTrumpPolicy(),
        fetchAlignment(),
        fetchCandidates(),
        fetchPerformance(),
        fetchRiseCycles()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchStrategies, fetchSectorMomentum, fetchTrumpPolicy, fetchAlignment, fetchCandidates, fetchPerformance, fetchRiseCycles]);

  // Strategy Card Component
  const StrategyCard = ({ strategy }) => (
    <div
      onClick={() => setSelectedStrategy(strategy)}
      style={{
        background: selectedStrategy?.strategy_id === strategy.strategy_id
          ? 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)'
          : '#1e293b',
        borderRadius: '12px',
        padding: '20px',
        cursor: 'pointer',
        border: selectedStrategy?.strategy_id === strategy.strategy_id
          ? '2px solid #3b82f6'
          : '1px solid #334155',
        transition: 'all 0.2s ease'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div>
          <h3 style={{ color: '#f1f5f9', margin: 0, fontSize: '18px' }}>{strategy.strategy_name}</h3>
          <span style={{
            background: strategy.strategy_type === 'intraday' ? '#22c55e' : '#3b82f6',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            marginTop: '4px',
            display: 'inline-block'
          }}>
            {strategy.strategy_type?.toUpperCase()}
          </span>
        </div>
        <span style={{
          color: strategy.is_active ? '#22c55e' : '#64748b',
          fontSize: '12px'
        }}>
          {strategy.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>
      <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 12px 0' }}>
        {strategy.description}
      </p>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {strategy.asset_types?.map((type, i) => (
          <span key={i} style={{
            background: '#334155',
            color: '#94a3b8',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px'
          }}>
            {type}
          </span>
        ))}
      </div>
    </div>
  );

  // Sector Momentum Card
  const SectorCard = ({ sector, rank }) => (
    <div
      onClick={() => fetchSectorStocks(sector.sector)}
      style={{
        background: selectedSector === sector.sector ? '#1e3a5f' : '#1e293b',
        borderRadius: '8px',
        padding: '12px 16px',
        cursor: 'pointer',
        border: selectedSector === sector.sector ? '1px solid #3b82f6' : '1px solid #334155',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            background: rank <= 3 ? '#22c55e' : rank <= 6 ? '#f59e0b' : '#64748b',
            color: 'white',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            {rank}
          </span>
          <span style={{ color: '#f1f5f9', fontWeight: '500' }}>{sector.sector}</span>
        </div>
      </div>
      <div style={{
        color: sector.avg_change_pct >= 0 ? '#22c55e' : '#ef4444',
        fontWeight: '600'
      }}>
        {sector.avg_change_pct >= 0 ? '+' : ''}{sector.avg_change_pct?.toFixed(2)}%
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', color: '#64748b' }}>
        Loading strategies...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ color: '#f1f5f9', margin: 0, fontSize: '28px' }}>Trading Strategies</h1>
        <p style={{ color: '#64748b', margin: '8px 0 0 0' }}>
          Manage strategies, analyze sectors, and detect rise cycles
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '24px',
        background: '#0f172a',
        padding: '4px',
        borderRadius: '8px',
        width: 'fit-content'
      }}>
        {['strategies', 'sectors', 'rise-cycles', 'candidates'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: activeTab === tab ? '#3b82f6' : 'transparent',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              color: activeTab === tab ? 'white' : '#64748b',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            {tab.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
          </button>
        ))}
      </div>

      {/* Strategies Tab */}
      {activeTab === 'strategies' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
            {strategies.map(strategy => (
              <StrategyCard key={strategy.strategy_id + strategy.created_at} strategy={strategy} />
            ))}
          </div>

          {/* Trump Policy Direction Matrix */}
          {trumpPolicy.sectors.length > 0 && (
            <div style={{ marginTop: '24px', background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155' }}>
              <h3 style={{ color: '#f1f5f9', margin: '0 0 16px 0', fontSize: '18px' }}>
                🏛️ Trump Policy Direction Matrix
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                {/* Winners */}
                <div>
                  <h4 style={{ color: '#22c55e', margin: '0 0 12px 0', fontSize: '14px' }}>▲ Policy Winners</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {trumpPolicy.sectors
                      .filter(s => (s.trump_bias_score || 0) >= 0)
                      .sort((a, b) => (b.trump_bias_score || 0) - (a.trump_bias_score || 0))
                      .map((s, i) => (
                        <div key={i} style={{ background: '#0f172a', borderRadius: '8px', padding: '12px', borderLeft: '3px solid #22c55e' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#f1f5f9', fontWeight: '500' }}>{s.sector}</span>
                            <span style={{ color: '#22c55e', fontWeight: '600', fontSize: '14px' }}>
                              +{(s.trump_bias_score || 0).toFixed(1)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                              Tariff: <span style={{ color: (s.tariff_sensitivity || 0) > 0.5 ? '#f59e0b' : '#64748b' }}>
                                {((s.tariff_sensitivity || 0) * 100).toFixed(0)}%
                              </span>
                            </span>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                              China: <span style={{ color: Math.abs(s.china_tariff_impact || 0) > 0.3 ? '#ef4444' : '#64748b' }}>
                                {((s.china_tariff_impact || 0) * 100).toFixed(0)}%
                              </span>
                            </span>
                          </div>
                          {s.risk_factors && (
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>{s.risk_factors}</div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
                {/* Losers */}
                <div>
                  <h4 style={{ color: '#ef4444', margin: '0 0 12px 0', fontSize: '14px' }}>▼ Policy Losers</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {trumpPolicy.sectors
                      .filter(s => (s.trump_bias_score || 0) < 0)
                      .sort((a, b) => (a.trump_bias_score || 0) - (b.trump_bias_score || 0))
                      .map((s, i) => (
                        <div key={i} style={{ background: '#0f172a', borderRadius: '8px', padding: '12px', borderLeft: '3px solid #ef4444' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#f1f5f9', fontWeight: '500' }}>{s.sector}</span>
                            <span style={{ color: '#ef4444', fontWeight: '600', fontSize: '14px' }}>
                              {(s.trump_bias_score || 0).toFixed(1)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                              Tariff: <span style={{ color: (s.tariff_sensitivity || 0) > 0.5 ? '#f59e0b' : '#64748b' }}>
                                {((s.tariff_sensitivity || 0) * 100).toFixed(0)}%
                              </span>
                            </span>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                              China: <span style={{ color: Math.abs(s.china_tariff_impact || 0) > 0.3 ? '#ef4444' : '#64748b' }}>
                                {((s.china_tariff_impact || 0) * 100).toFixed(0)}%
                              </span>
                            </span>
                          </div>
                          {s.risk_factors && (
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>{s.risk_factors}</div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              {/* Recent Political News */}
              {trumpPolicy.news.length > 0 && (
                <div>
                  <h4 style={{ color: '#94a3b8', margin: '0 0 12px 0', fontSize: '14px' }}>Recent Political Headlines</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {trumpPolicy.news.slice(0, 8).map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#0f172a', borderRadius: '6px' }}>
                        <span style={{
                          width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                          background: (item.sentiment_score || 0) > 0.2 ? '#22c55e' : (item.sentiment_score || 0) < -0.2 ? '#ef4444' : '#f59e0b'
                        }} />
                        <span style={{ color: '#d1d5db', fontSize: '13px', flex: 1 }}>{item.headline}</span>
                        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                          {item.trump_related && <span style={{ background: '#7c3aed', color: 'white', padding: '1px 6px', borderRadius: '3px', fontSize: '10px' }}>TRUMP</span>}
                          {item.tariff_related && <span style={{ background: '#f59e0b', color: 'black', padding: '1px 6px', borderRadius: '3px', fontSize: '10px' }}>TARIFF</span>}
                          {item.fed_related && <span style={{ background: '#3b82f6', color: 'white', padding: '1px 6px', borderRadius: '3px', fontSize: '10px' }}>FED</span>}
                          {item.china_related && <span style={{ background: '#ef4444', color: 'white', padding: '1px 6px', borderRadius: '3px', fontSize: '10px' }}>CHINA</span>}
                        </div>
                        <span style={{ color: '#64748b', fontSize: '11px', flexShrink: 0 }}>{item.source}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Alignment Gauge */}
          {alignment && alignment.alignment_effectiveness && (
            <div style={{ marginTop: '16px', background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155' }}>
              <h3 style={{ color: '#f1f5f9', margin: '0 0 16px 0', fontSize: '18px' }}>
                🎯 Multi-Timeframe Alignment Gauge
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                {alignment.alignment_effectiveness.map((row, i) => {
                  const allAligned = row.all_tf_aligned;
                  const cascade = row.momentum_cascade;
                  const label = allAligned && cascade ? 'Full Cascade' : allAligned ? 'All Aligned' : cascade ? 'Cascade Only' : 'Partial';
                  const color = allAligned && cascade ? '#22c55e' : allAligned ? '#3b82f6' : '#f59e0b';
                  return (
                    <div key={i} style={{ background: '#0f172a', borderRadius: '8px', padding: '16px', borderTop: `3px solid ${color}` }}>
                      <div style={{ color, fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>{label}</div>
                      <div style={{ color: '#f1f5f9', fontSize: '28px', fontWeight: 'bold' }}>{((row.up_pct || 0) * 100).toFixed(1)}%</div>
                      <div style={{ color: '#64748b', fontSize: '12px' }}>Win Rate ({row.total_signals || 0} signals)</div>
                      <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '4px' }}>
                        Avg Score: {(row.avg_nested_score || 0).toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Timeframe Breakdown */}
              {alignment.timeframe_breakdown && (
                <div>
                  <h4 style={{ color: '#94a3b8', margin: '0 0 12px 0', fontSize: '14px' }}>Timeframe Factors</h4>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {alignment.timeframe_breakdown.map((tf, i) => (
                      <div key={i} style={{
                        background: '#0f172a', borderRadius: '8px', padding: '10px 16px',
                        border: `1px solid ${tf.is_bullish ? '#22c55e' : '#ef4444'}40`
                      }}>
                        <div style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: '500' }}>{tf.factor}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                          <span style={{ color: tf.is_bullish ? '#22c55e' : '#ef4444', fontSize: '12px' }}>
                            {tf.is_bullish ? '▲ Bullish' : '▼ Bearish'}
                          </span>
                          <span style={{ color: '#64748b', fontSize: '11px' }}>
                            {((tf.up_pct || 0) * 100).toFixed(0)}% win ({tf.total_signals || 0})
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Insight */}
              {alignment.key_insight && (
                <div style={{ marginTop: '12px', background: '#0f172a', borderRadius: '8px', padding: '12px 16px', borderLeft: '3px solid #a855f7' }}>
                  <span style={{ color: '#a855f7', fontSize: '12px', fontWeight: '600' }}>KEY INSIGHT</span>
                  <p style={{ color: '#d1d5db', fontSize: '13px', margin: '4px 0 0 0' }}>{alignment.key_insight}</p>
                </div>
              )}
            </div>
          )}

          {/* Win Rate Summary */}
          {performanceData && (
            <div style={{ background: '#1e293b80', borderRadius: '12px', padding: '16px', border: '1px solid #334155', marginTop: '16px' }}>
              <h4 style={{ color: '#22c55e', margin: '0 0 12px', fontSize: '14px' }}>Historical Win Rates</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                {[
                  { label: '7 Day', key: 'win_rate_7d', fallback: 'short_term_accuracy' },
                  { label: '30 Day', key: 'win_rate_30d', fallback: 'medium_term_accuracy' },
                  { label: '90 Day', key: 'win_rate_90d', fallback: 'long_term_accuracy' },
                ].map(wr => {
                  const val = performanceData[wr.key] || performanceData[wr.fallback] || performanceData.overall_accuracy || 0;
                  const pct = typeof val === 'number' && val <= 1 ? (val * 100).toFixed(1) : Number(val).toFixed(1);
                  return (
                    <div key={wr.label} style={{ background: '#0f172a', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                      <div style={{ color: '#64748b', fontSize: '11px' }}>{wr.label}</div>
                      <div style={{ color: Number(pct) >= 55 ? '#10b981' : Number(pct) >= 45 ? '#f59e0b' : '#ef4444', fontSize: '24px', fontWeight: 700 }}>{pct}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Strategy Help Panel */}
          {selectedStrategy && (
            <div style={{
              marginTop: '24px',
              background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
              borderRadius: '16px',
              padding: '24px',
              border: '2px solid #3b82f6'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: '24px' }}>{selectedStrategy.strategy_name}</h2>
                  <span style={{
                    background: selectedStrategy.strategy_type === 'intraday' ? '#22c55e' : '#3b82f6',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    marginTop: '8px',
                    display: 'inline-block'
                  }}>
                    {selectedStrategy.strategy_type?.toUpperCase()}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedStrategy(null)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#64748b',
                    fontSize: '24px',
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Strategy Description */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#3b82f6', margin: '0 0 12px 0', fontSize: '16px' }}>How This Strategy Works</h3>
                <p style={{ color: '#94a3b8', fontSize: '15px', lineHeight: '1.6', margin: 0 }}>
                  {selectedStrategy.description}
                </p>
              </div>

              {/* Detailed Help based on strategy type */}
              {selectedStrategy.strategy_code === 'MTC' && (
                <div style={{ background: '#0f172a', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                  <h4 style={{ color: '#22c55e', margin: '0 0 16px 0' }}>📊 Multi-Timeframe Confirmation Strategy</h4>
                  <div style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.8' }}>
                    <p><strong>Goal:</strong> Only enter trades when weekly, daily, and hourly trends all align in the same direction.</p>
                    <p><strong>Entry Rules:</strong></p>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Weekly trend must be UP (price above 20-week SMA)</li>
                      <li>Daily trend must be UP (price above 20-day SMA)</li>
                      <li>Hourly gives entry signal (RSI oversold bounce or MACD crossover)</li>
                    </ul>
                    <p><strong>Exit Rules:</strong></p>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Daily trend turns DOWN or hits target</li>
                      <li>Stop loss: Below recent swing low</li>
                    </ul>
                    <p><strong>Best For:</strong> Swing traders looking for high-probability entries with reduced risk.</p>
                  </div>
                </div>
              )}

              {selectedStrategy.strategy_code === 'SMR' && (
                <div style={{ background: '#0f172a', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                  <h4 style={{ color: '#f59e0b', margin: '0 0 16px 0' }}>🔄 Sector Momentum Rotation Strategy</h4>
                  <div style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.8' }}>
                    <p><strong>Goal:</strong> Ride the strongest sectors by rotating into top-performing industries weekly.</p>
                    <p><strong>How It Works:</strong></p>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Every week, rank all 11 sectors by average stock performance</li>
                      <li>Focus on TOP 3 sectors showing positive momentum</li>
                      <li>Within those sectors, find the top 10-20 stocks by weekly % gain</li>
                      <li>Enter positions in leaders, exit when sector loses momentum</li>
                    </ul>
                    <p><strong>Key Metrics:</strong></p>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Avg Change %: Average price change of stocks in sector</li>
                      <li>Momentum Rank: 1-11 ranking (1 = strongest)</li>
                      <li>Volatility: Higher = more opportunity but more risk</li>
                    </ul>
                    <p><strong>Best For:</strong> Position traders who want to be in the hottest sectors each week.</p>
                  </div>
                </div>
              )}

              {selectedStrategy.strategy_code === 'RCD' && (
                <div style={{ background: '#0f172a', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                  <h4 style={{ color: '#ef4444', margin: '0 0 16px 0' }}>🚀 Rise Cycle Detection Strategy</h4>
                  <div style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.8' }}>
                    <p><strong>Goal:</strong> Catch intraday momentum surges and exit before they reverse.</p>
                    <p><strong>How It Works:</strong></p>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Monitor 5-minute candles for "rise cycle" start signals</li>
                      <li>Rise cycle starts when: 3+ consecutive green candles OR volume spike + price break</li>
                      <li>Enter at cycle confirmation, ride the momentum</li>
                      <li>Exit when: First red candle, or RSI divergence, or target hit</li>
                    </ul>
                    <p><strong>Risk Management:</strong></p>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      <li>Position size: 1-2% of account per trade</li>
                      <li>Stop loss: Below the cycle start low</li>
                      <li>Typical trade duration: 15-90 minutes</li>
                    </ul>
                    <p><strong>Best For:</strong> Day traders who want quick, momentum-based entries and exits.</p>
                  </div>
                </div>
              )}

              {/* Timeframes and Asset Types */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ background: '#0f172a', borderRadius: '8px', padding: '16px' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '8px' }}>Timeframes</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {selectedStrategy.timeframes?.map((tf, i) => (
                      <span key={i} style={{
                        background: '#334155',
                        color: '#f1f5f9',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}>
                        {tf}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{ background: '#0f172a', borderRadius: '8px', padding: '16px' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '8px' }}>Asset Types</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {selectedStrategy.asset_types?.map((type, i) => (
                      <span key={i} style={{
                        background: '#334155',
                        color: '#f1f5f9',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}>
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sectors Tab */}
      {activeTab === 'sectors' && (
        <div>
          {/* Sector Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
            <div style={{ background: '#1e293b', borderRadius: '10px', padding: '16px', border: '1px solid #334155' }}>
              <div style={{ color: '#64748b', fontSize: '12px' }}>Total Sectors</div>
              <div style={{ color: '#f1f5f9', fontSize: '28px', fontWeight: 'bold' }}>{sectorMomentum.length}</div>
            </div>
            <div style={{ background: '#1e293b', borderRadius: '10px', padding: '16px', border: '1px solid #334155' }}>
              <div style={{ color: '#64748b', fontSize: '12px' }}>Bullish Sectors</div>
              <div style={{ color: '#22c55e', fontSize: '28px', fontWeight: 'bold' }}>{sectorMomentum.filter(s => s.avg_change_pct >= 0).length}</div>
            </div>
            <div style={{ background: '#1e293b', borderRadius: '10px', padding: '16px', border: '1px solid #334155' }}>
              <div style={{ color: '#64748b', fontSize: '12px' }}>Bearish Sectors</div>
              <div style={{ color: '#ef4444', fontSize: '28px', fontWeight: 'bold' }}>{sectorMomentum.filter(s => s.avg_change_pct < 0).length}</div>
            </div>
            <div style={{ background: '#1e293b', borderRadius: '10px', padding: '16px', border: '1px solid #334155' }}>
              <div style={{ color: '#64748b', fontSize: '12px' }}>Top Sector</div>
              <div style={{ color: '#22c55e', fontSize: '18px', fontWeight: 'bold' }}>{sectorMomentum[0]?.sector || '-'}</div>
            </div>
          </div>

          {/* Sector Momentum Heatmap Bar */}
          <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', marginBottom: '24px', border: '1px solid #334155' }}>
            <h3 style={{ color: '#f1f5f9', margin: '0 0 16px 0', fontSize: '16px' }}>Sector Momentum Heatmap</h3>
            <div style={{ display: 'flex', gap: '4px', height: '60px', alignItems: 'flex-end' }}>
              {sectorMomentum.map((sector, idx) => {
                const maxAbs = Math.max(...sectorMomentum.map(s => Math.abs(s.avg_change_pct || 0)), 1);
                const barHeight = Math.max(10, (Math.abs(sector.avg_change_pct || 0) / maxAbs) * 50);
                const isPositive = (sector.avg_change_pct || 0) >= 0;
                return (
                  <div key={idx} onClick={() => fetchSectorStocks(sector.sector)} style={{ flex: 1, cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{
                      height: `${barHeight}px`,
                      background: isPositive
                        ? `rgba(34, 197, 94, ${0.3 + (sector.avg_change_pct / maxAbs) * 0.7})`
                        : `rgba(239, 68, 68, ${0.3 + (Math.abs(sector.avg_change_pct) / maxAbs) * 0.7})`,
                      borderRadius: '4px 4px 0 0',
                      border: selectedSector === sector.sector ? '2px solid #3b82f6' : 'none',
                      transition: 'all 0.2s'
                    }} />
                    <div style={{ color: '#64748b', fontSize: '9px', marginTop: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {sector.sector?.split(' ')[0]}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div>
              <h3 style={{ color: '#f1f5f9', marginTop: 0 }}>Sector Momentum Rankings</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {sectorMomentum.map((sector, idx) => (
                  <SectorCard key={sector.sector} sector={sector} rank={idx + 1} />
                ))}
              </div>
            </div>
            <div>
              <h3 style={{ color: '#f1f5f9', marginTop: 0 }}>
                {selectedSector ? `Top Stocks in ${selectedSector}` : 'Click a Sector to View Top Stocks'}
              </h3>
              {sectorStocks.length > 0 ? (
                <div style={{ background: '#1e293b', borderRadius: '12px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#0f172a' }}>
                        <th style={{ color: '#64748b', padding: '12px', textAlign: 'left' }}>Symbol</th>
                        <th style={{ color: '#64748b', padding: '12px', textAlign: 'right' }}>Price</th>
                        <th style={{ color: '#64748b', padding: '12px', textAlign: 'right' }}>Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectorStocks.map(stock => (
                        <tr key={stock.symbol} style={{ borderBottom: '1px solid #334155' }}>
                          <td style={{ color: '#f1f5f9', padding: '12px', fontWeight: '500' }}>{stock.symbol}</td>
                          <td style={{ color: '#94a3b8', padding: '12px', textAlign: 'right' }}>${stock.price?.toFixed(2)}</td>
                          <td style={{ color: stock.week_change_pct >= 0 ? '#22c55e' : '#ef4444', padding: '12px', textAlign: 'right', fontWeight: '600' }}>
                            {stock.week_change_pct >= 0 ? '+' : ''}{stock.week_change_pct}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ background: '#1e293b', borderRadius: '12px', padding: '40px', textAlign: 'center', border: '1px solid #334155' }}>
                  <div style={{ color: '#64748b', fontSize: '40px', marginBottom: '12px' }}>📊</div>
                  <p style={{ color: '#94a3b8', margin: 0 }}>Select a sector from the rankings to see top-performing stocks</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rise Cycles Tab */}
      {activeTab === 'rise-cycles' && (
        <div>
          {/* Quick Symbol Picker */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {['NVDA', 'TSLA', 'AAPL', 'MSFT', 'AMD', 'META', 'AMZN', 'GOOGL', 'BTCUSD', 'ETHUSD'].map(sym => (
              <button
                key={sym}
                onClick={() => { setCycleSymbol(sym); }}
                style={{
                  background: cycleSymbol === sym ? '#3b82f6' : '#1e293b',
                  border: cycleSymbol === sym ? '1px solid #3b82f6' : '1px solid #334155',
                  borderRadius: '6px', padding: '6px 14px', color: cycleSymbol === sym ? 'white' : '#94a3b8',
                  cursor: 'pointer', fontSize: '13px', fontWeight: '500'
                }}
              >
                {sym}
              </button>
            ))}
          </div>

          <div style={{ background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155' }}>
            <h3 style={{ color: '#f1f5f9', marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              Rise Cycle Analyzer
              <span style={{ background: '#3b82f620', color: '#3b82f6', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>
                {cycleSymbol}
              </span>
            </h3>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center' }}>
              <input
                type="text"
                value={cycleSymbol}
                onChange={(e) => setCycleSymbol(e.target.value.toUpperCase())}
                placeholder="Symbol"
                style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', padding: '8px 12px', color: '#f1f5f9', width: '100px' }}
              />
              <input
                type="date"
                value={cycleDate}
                onChange={(e) => setCycleDate(e.target.value)}
                style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', padding: '8px 12px', color: '#f1f5f9' }}
              />
              <button
                onClick={fetchRiseCycles}
                style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)', border: 'none', borderRadius: '6px', padding: '8px 20px', color: 'white', cursor: 'pointer', fontWeight: '500' }}
              >
                Analyze
              </button>
            </div>

            {riseCycles?.note && (
              <div style={{ background: '#f59e0b20', border: '1px solid #f59e0b40', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', color: '#f59e0b', fontSize: '13px' }}>
                {riseCycles.note}
              </div>
            )}

            {riseCycles?.summary && (
              <>
                {/* Summary Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                    <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>Total Cycles</div>
                    <div style={{ color: '#f1f5f9', fontSize: '28px', fontWeight: 'bold' }}>{riseCycles.summary.total_cycles}</div>
                  </div>
                  <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                    <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>Winning</div>
                    <div style={{ color: '#22c55e', fontSize: '28px', fontWeight: 'bold' }}>{riseCycles.summary.winning_cycles}</div>
                  </div>
                  <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                    <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>Losing</div>
                    <div style={{ color: '#ef4444', fontSize: '28px', fontWeight: 'bold' }}>{riseCycles.summary.losing_cycles}</div>
                  </div>
                  <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                    <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>End Capital</div>
                    <div style={{ color: '#f1f5f9', fontSize: '24px', fontWeight: 'bold' }}>${riseCycles.summary.ending_capital?.toLocaleString()}</div>
                  </div>
                  <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                    <div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>Return</div>
                    <div style={{ color: riseCycles.summary.total_return_pct >= 0 ? '#22c55e' : '#ef4444', fontSize: '28px', fontWeight: 'bold' }}>
                      {riseCycles.summary.total_return_pct >= 0 ? '+' : ''}{riseCycles.summary.total_return_pct?.toFixed(2)}%
                    </div>
                  </div>
                </div>

                {/* Win Rate Visual Bar */}
                <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#94a3b8', fontSize: '13px' }}>Win Rate</span>
                    <span style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: '600' }}>
                      {riseCycles.summary.total_cycles > 0 ? ((riseCycles.summary.winning_cycles / riseCycles.summary.total_cycles) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div style={{ height: '12px', background: '#334155', borderRadius: '6px', overflow: 'hidden', display: 'flex' }}>
                    <div style={{
                      width: `${riseCycles.summary.total_cycles > 0 ? (riseCycles.summary.winning_cycles / riseCycles.summary.total_cycles) * 100 : 0}%`,
                      background: 'linear-gradient(90deg, #22c55e, #10b981)',
                      borderRadius: '6px 0 0 6px',
                      transition: 'width 0.5s'
                    }} />
                    <div style={{
                      width: `${riseCycles.summary.total_cycles > 0 ? (riseCycles.summary.losing_cycles / riseCycles.summary.total_cycles) * 100 : 0}%`,
                      background: 'linear-gradient(90deg, #ef4444, #dc2626)',
                      borderRadius: '0 6px 6px 0'
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                    <span style={{ color: '#22c55e', fontSize: '11px' }}>Avg Win: {riseCycles.summary.avg_win_pct ? `+${riseCycles.summary.avg_win_pct.toFixed(1)}%` : 'N/A'}</span>
                    <span style={{ color: '#ef4444', fontSize: '11px' }}>Avg Loss: {riseCycles.summary.avg_loss_pct ? `${riseCycles.summary.avg_loss_pct.toFixed(1)}%` : 'N/A'}</span>
                  </div>
                </div>

                {/* How It Works */}
                <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', borderLeft: '3px solid #a855f7' }}>
                  <h4 style={{ color: '#a855f7', margin: '0 0 8px 0', fontSize: '14px' }}>How Rise Cycle Detection Works</h4>
                  <div style={{ color: '#94a3b8', fontSize: '13px', lineHeight: '1.6' }}>
                    <p style={{ margin: '0 0 8px 0' }}>Rise cycles identify intraday momentum surges by monitoring 5-minute candle patterns. A cycle starts when 3+ consecutive green candles appear with increasing volume.</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '8px' }}>
                      <div style={{ background: '#1e293b', borderRadius: '6px', padding: '10px' }}>
                        <div style={{ color: '#22c55e', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Entry Signal</div>
                        <div style={{ color: '#94a3b8', fontSize: '12px' }}>3+ green candles + volume spike + price breaks above short-term resistance</div>
                      </div>
                      <div style={{ background: '#1e293b', borderRadius: '6px', padding: '10px' }}>
                        <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Exit Signal</div>
                        <div style={{ color: '#94a3b8', fontSize: '12px' }}>First red candle, RSI divergence, or target profit hit</div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Candidates Tab */}
      {activeTab === 'candidates' && (
        <div>
          <div style={{ background: '#1e293b', borderRadius: '12px', overflow: 'hidden' }}>
            <h3 style={{ color: '#f1f5f9', margin: 0, padding: '16px 20px 12px', fontSize: '18px' }}>Top 20 Rise Cycle Candidates</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0f172a' }}>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'left', fontSize: '12px' }}>Rank</th>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'left', fontSize: '12px' }}>Symbol</th>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'right', fontSize: '12px' }}>Price</th>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'right', fontSize: '12px' }}>Growth Score</th>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'right', fontSize: '12px' }}>RSI</th>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'center', fontSize: '12px' }}>EMA Status</th>
                  <th style={{ color: '#64748b', padding: '12px', textAlign: 'center', fontSize: '12px' }}>Signal</th>
                </tr>
              </thead>
              <tbody>
                {candidates.slice(0, 20).map((c, idx) => (
                  <tr key={c.symbol || idx} style={{ borderBottom: '1px solid #334155' }}>
                    <td style={{ color: '#94a3b8', padding: '12px' }}>
                      <span style={{
                        background: idx < 3 ? '#22c55e' : idx < 10 ? '#f59e0b' : '#64748b',
                        color: 'white',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {idx + 1}
                      </span>
                    </td>
                    <td style={{ color: '#f1f5f9', padding: '12px', fontWeight: '600' }}>{c.symbol}</td>
                    <td style={{ color: '#94a3b8', padding: '12px', textAlign: 'right' }}>
                      ${(c.price || c.current_price || 0).toFixed(2)}
                    </td>
                    <td style={{ color: '#f1f5f9', padding: '12px', textAlign: 'right', fontWeight: '600' }}>
                      <span style={{
                        color: (c.growth_score || c.score || 0) >= 70 ? '#22c55e' : (c.growth_score || c.score || 0) >= 40 ? '#f59e0b' : '#ef4444'
                      }}>
                        {(c.growth_score || c.score || 0).toFixed(1)}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <span style={{
                        color: (c.rsi || c.rsi_14 || 0) > 70 ? '#ef4444' : (c.rsi || c.rsi_14 || 0) < 30 ? '#22c55e' : '#94a3b8'
                      }}>
                        {(c.rsi || c.rsi_14 || 0).toFixed(1)}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{
                        background: (c.ema_status || c.ema_trend || '').toLowerCase().includes('bull') ? '#22c55e20' : '#ef444420',
                        color: (c.ema_status || c.ema_trend || '').toLowerCase().includes('bull') ? '#22c55e' : '#ef4444',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px'
                      }}>
                        {c.ema_status || c.ema_trend || 'N/A'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{
                        background: (c.signal || c.action || '').toLowerCase().includes('buy') ? '#22c55e' : (c.signal || c.action || '').toLowerCase().includes('sell') ? '#ef4444' : '#f59e0b',
                        color: 'white',
                        padding: '2px 10px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        {c.signal || c.action || 'WATCH'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {candidates.length === 0 && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                No candidates available. The API may be loading or unavailable.
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

